import { useCallback, useEffect, useState } from "react";
import { Package, AlertTriangle, XCircle } from "lucide-react";
import api from "../../lib/api";
import { BRAND_COLORS, STATUS_COLORS } from "../../lib/bookingStatus";
import { deDate } from "../../lib/dateFormat";
import { coverageWarningMessage } from "../../lib/servicePackageLimits";
import ServicePackageCancelDialog from "../customer/ServicePackageCancelDialog";

/**
 * Feb 2026 · Admin-/Team-Einsicht: gebuchtes Service-Paket pro Objekt.
 *
 * Zeigt für jedes Objekt des Kunden das zuletzt verbindlich gebuchte Paket.
 * Admins (`canCancel`) können jedes gebuchte Paket kündigen — das Backend
 * erzwingt, dass die Betreuung nie vor dem Ende einer zukünftigen Abwesenheit
 * endet.
 *
 * Datenquelle: GET /api/service-packages/bookings?customer_id=<id>
 */
export function ServicePackagesCard({ customerId, properties, canCancel = false }) {
  const [bookings, setBookings] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/service-packages/bookings", { params: { customer_id: customerId } });
      setBookings(data || []);
    } catch (e) {
      setBookings([]);
    }
  }, [customerId]);

  useEffect(() => { reload(); }, [reload]);

  const props = properties || [];
  if (props.length === 0) return null;

  const bookingFor = (propertyId) =>
    (bookings || []).find((b) => b.property_id === propertyId) || null;

  return (
    <section className="mb-6 bg-white border border-navy/10 p-5" data-testid="customer-service-packages-card">
      <div className="flex items-center gap-2.5 mb-4">
        <Package className="w-4 h-4 text-navy-dark/60" strokeWidth={1.8} />
        <div className="label-caps">Service Pakete pro Objekt</div>
      </div>

      {bookings === null ? (
        <div className="label-caps text-navy-dark/40">Wird geladen …</div>
      ) : (
        <ul className="space-y-2" data-testid="customer-service-packages-list">
          {props.map((p) => {
            const b = bookingFor(p.id);
            const cancellable = canCancel && b && b.status === "booked";
            return (
              <li
                key={p.id}
                data-testid={`service-package-property-${p.id}`}
                className="border border-navy/10 bg-white px-4 py-3 flex items-center gap-3 flex-wrap md:flex-nowrap"
                style={{ borderLeft: `3px solid ${b?.absence_conflict ? STATUS_COLORS.red : b ? BRAND_COLORS.gold : "rgba(10,20,40,0.12)"}` }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] text-navy font-medium truncate">{p.name || "Objekt"}</div>
                  {b ? (
                    <div className="text-[11px] text-navy-dark/60 mt-0.5 truncate" data-testid={`service-package-property-${p.id}-package`}>
                      {b.package_main}
                      <span className="opacity-70"> · {b.package_note}</span>
                      {b.start_date && (
                        <span className="opacity-70"> · {b.end_date ? `${deDate(b.start_date)}–${deDate(b.end_date)}` : `ab ${deDate(b.start_date)}`}</span>
                      )}
                      <span className="opacity-70"> · gebucht {deDate(b.created_at)}</span>
                      {b.status === "cancelled" && (
                        <span className="font-medium" style={{ color: STATUS_COLORS.red }} data-testid={`service-package-property-${p.id}-cancelled`}>
                          {" "}· gekündigt
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="text-[11px] text-navy-dark/45 mt-0.5 italic" data-testid={`service-package-property-${p.id}-package`}>
                      Kein Paket gebucht
                    </div>
                  )}
                  {b?.absence_conflict && (
                    <div className="mt-1.5 flex items-start gap-1.5" data-testid={`service-package-property-${p.id}-conflict`}>
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: STATUS_COLORS.red }} strokeWidth={1.8} />
                      <span className="text-[11px] leading-snug" style={{ color: STATUS_COLORS.red }}>
                        {coverageWarningMessage(b.absence_conflict.from_date, b.absence_conflict.to_date, true)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {b ? (
                    <span
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[0.12em] uppercase"
                      style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
                      data-testid={`service-package-property-${p.id}-badge`}
                    >
                      {b.package_title}
                    </span>
                  ) : (
                    <span className="text-[10px] tracking-[0.12em] uppercase text-navy-dark/40">
                      —
                    </span>
                  )}
                  {cancellable && (
                    <button
                      type="button"
                      onClick={() => setCancelTarget(b)}
                      data-testid={`service-package-property-${p.id}-cancel`}
                      className="inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] tracking-[0.12em] uppercase transition-colors hover:bg-red-50"
                      style={{ borderColor: STATUS_COLORS.red, color: STATUS_COLORS.red }}
                    >
                      <XCircle className="w-3.5 h-3.5" strokeWidth={1.8} />
                      Kündigen
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {cancelTarget && (
        <ServicePackageCancelDialog
          booking={cancelTarget}
          isAdmin
          de
          onClose={() => setCancelTarget(null)}
          onCancelled={reload}
        />
      )}
    </section>
  );
}
